import AutocompleteCombobox from '../combobox/autocomplete-combobox';

type DrData = {
  mode: string;
  manufacturer: string;
  year: string;
  model: string;
  vehicle: string;
  'stage-language:': string;
  tyreSelectorVersion: string;
  // These are probably old not-fitment params:
  lang?: string;
};

type DrResponse = {
  data: {
    text: string;
    value: string;
  }[];
}

type State = {
  manufacturer: string | null,
  year: number | null,
  model: string | null,
  vehicle: string | null,
};

declare global {
  interface Window {
    driveRightLangCode: string;
    driveRightCountryCode: string;
  }
}

export default class TyreSelectorByModel {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    manufacturer: HTMLSelectElement,
    year: HTMLSelectElement,
    model: HTMLSelectElement,
    vehicle: HTMLSelectElement,
    errorMessage: HTMLElement,
  };

  parent;

  initValues = {
    selectValues: [] as (string | number)[],
    selectKeys: [] as string[],
  };

  // Tyre selector filters state
  #state: State = {
    manufacturer: null,
    year: null,
    model: null,
    vehicle: null,
  };

  #comboboxes = {};

  #storageKey = 'ts__model';
  #updatingFilters = false;
  #submitting = false;

  drModes = ['years', 'models', 'vehicles', 'tyres']; // note: "manufacurers" is _not_ on this list. It has it's own functions.
  unwantedParameters = ['tyreSize', 'mobileTyreSize']; // these form element names should not be added to ajax requests or cache keys.

  // Contains tyre size data from DriveRight/Fitment (after form is completed)
  sizesResults: any[] = [];

  controller: AbortController = new AbortController();

  /**
   * Tyre selector by car model module
   *
   * @param {HTMLElement} element - Root element of the tyre selector
   * @param {TyreSelector} parent - Parent tyre selector instance
   */
  constructor(element: HTMLElement, parent) {
    this.parent = parent;
    this.dom = {} as any;
    this.dom.root = element;
    this.dom.form = element.querySelector('form[name="modelSearchForm"]') as HTMLFormElement;

    if (this.dom.form) {
      Object.keys(this.#state).forEach(key => {
        this.dom[key] = this.dom.form.querySelector(`select[name="${key}"]`) as HTMLSelectElement;
      });

      this.dom.errorMessage = this.dom.form.querySelector('[data-ts-error-message]') as HTMLElement;
    }

    if (Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary data and dom elements found
      console.log('[TyreSelectorByModel]: init model module');
      this.#initState();
      this.#initDropdowns();
      this.#attachEventListeners();
    } else {
      console.error('[TyreSelectorByModel]: missing data or dom elements');
    }
  }

  /**
   * Initialises state from dom select values
   */
  #initState() {
    // Default state
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if (this.dom[key]) {
        const value = this.getSelectValue(this.dom[key]);
        initialState[key] = value;
      }
    });

    // Check storage for user's previous values
    const storageValues = sessionStorage.getItem(this.#storageKey);
    if (storageValues) {
      try {
        const storageState = JSON.parse(storageValues);
        Object.keys(storageState).forEach(key => {
          if (this.dom[key]) {
            initialState[key] = storageState[key];
          }
        });
      } catch (e) {
        console.error('[TyreSelectorBySize]: Failed to parse storage values', e);
      }
    }

    this.updateState(initialState);
    this.populateForm(initialState);
  }

  /**
   * Initialises custom dropdowns for native selects
   */
  #initDropdowns() {
    Object.keys(this.#state)
      .forEach(key => {
        const selectEl = this.dom[key];
        this.#comboboxes[key] = new AutocompleteCombobox({
          select: selectEl,
          cssClassPrefix: 'combobox',
          browserAutocomplete: 'off'
        });
      });
  }

  /**
   * Attach event listeners to relevant dom elements
   */
  #attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));

    Object.keys(this.#state).forEach(key => {
      this.dom[key].addEventListener('change', this.onSelectChange.bind(this));
    });
  }

  /**
   * Handles form submit event
   */
  onFormSubmit(event: Event) {
    event.preventDefault();

    if (this.#submitting) {
      return;
    }

    this.#submitting = true;
    this.parent.setLoading(true);

    // Store values to session storage
    sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#state));

    if (this.sizesResults.length) {
      this.doAction(this.sizesResults);
    } else {
      //TODO: no sizes found, maybe show message?
    }
  }

  /**
   * Clear form values and session storage
   */
  onFormReset(event?: Event) {
    const values = this.getState();

    for (let key in values) {
      values[key] = null;
    }

    sessionStorage.removeItem(this.#storageKey);
    this.parent.setView(null);
    this.parent.setVisibility('visible');

    this.sizesResults = [];
    this.updateState(values);
    this.populateResults(); // without data this hides any visible results in search area

    const allSelects = Object.keys(values).map(key => this.dom[key]) as HTMLSelectElement[];

    allSelects.forEach((select, selectIndex) => {
      select.value = '';

      if (selectIndex > 0) {
        select.disabled = true;
      }

      // Trigger native event on all elements that were changed
      // Combobox needs this, if it's used
      this.#updatingFilters = true;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#updatingFilters = false;
    });

    // Clear tyreData
    this.parent.storeTyreData({}, 'model');
  }

  #serializeForm(form) {
    const formData = new FormData(form);
    const serializedArray: any[] = [];

    formData.forEach((value, key) => {
      serializedArray.push({ name: key, value: value });
    });

    return serializedArray;
  }

  /**
   * Handles native select element change event
   */
  onSelectChange(event: Event) {
    // Callback was caused by internal change in filters
    if (this.#updatingFilters) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    const selectName = select.name;
    const selectValue = this.getSelectValue(select);
    const selectIndex = Object.keys(this.#state).indexOf(select.name);

    console.log('[TyreSelectorByModel]: onSelectChange', { selectName, selectValue, selectIndex });

    const newState = this.getState();
    let resetFields = false;
    for (let key in newState) {
      if (selectName === key) {
        newState[selectName] = selectValue;
        resetFields = true;
      } else if (resetFields) {
        newState[key] = null;
      }
    }

    this.updateState(newState);

    const paramObj: any = {
      'stage-language': this.parent.selectedLang,
      tyreSelectorVersion: this.parent.version,
    };

    this.setSubmitActive(false);

    if (!selectValue) {
      this.checkSubmitState();

      // Disable next selects
      const allSelects = Object.keys(this.#state).map(key => this.dom[key]) as HTMLSelectElement[];
      const nextSelects = allSelects.slice(selectIndex + 1);
      nextSelects.forEach(nextSelect => {
        nextSelect.disabled = true;
        // Remove all options except the empty value one
        Array.from(nextSelect.options).filter((opt) => opt.value !== '').forEach((opt) => {
          opt.remove();
        });
      });
    } else {
      // Set ajax parameters
      paramObj.mode = this.drModes[selectIndex];
      const formValues = this.#serializeForm(this.dom.form);
      formValues.forEach((kv) => {
        if (this.unwantedParameters.indexOf(kv.name) === -1) {
          paramObj[kv.name] = kv.value;
        }
      });

      this.getDataAndPopulate(selectIndex, paramObj);
    }
  }

  /**
   * Returns current filter values
   */
  getState() {
    return { ...this.#state };
  }


  updateState(changedState: Partial<State>) {
    const oldState = this.getState();
    const futureState = { ...oldState, ...changedState };

    console.log('updating state', { oldState, changedState, futureState });

    this.#state = futureState;
  }

  /**
   * Formats select value to number or null
   * All values are strings in select elements, make empty string null and others as strings
   */
  getSelectValue(select: HTMLSelectElement) {
    return select.value === '' ? null : String(select.value);
  }

  getSessionData(paramObj, returnDefaultSizeData?: any) {
    let data;
    let key;

    if (!window.sessionStorage) {
      return false;
    }

    // Return defaultSizeData if it was requested
    if (returnDefaultSizeData) {
      if (!paramObj.vehicle) {
        return false;
      }

      key = 'primaryOption_' + paramObj.vehicle;
    }
    // Other data
    else {
      key = this.#getSessionKeyFromObject(paramObj);
    }

    try {
      data = sessionStorage.getItem(key);
      data = JSON.parse(data);
      return data;
    } catch (e) {
      return false;
    }
  }

  storeSessionData(data, paramObj) {
    const key = this.#getSessionKeyFromObject(paramObj);
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  #getSessionKeyFromObject(object) {
    const ignoredProps = object.tyreSelectorVersion === 'fitment' ? ['tyreSize', 'lang'] : ['tyreSize', 'stage-language'];
    const propMap = {
      'stage-language': 'l',
      tyreSelectorVersion: 'tsv',
    };

    let key = '';
    Object.keys(object).forEach(prop => {
      if (ignoredProps.includes(prop)) {
        return;
      }

      // Replace prop with shortend name (if shortend is not defined in paramObj)
      const propName = propMap[prop] && typeof object[propMap[prop]] === 'undefined' ? propMap[prop] : prop;

      // Convert prop value to string
      let value = String(object[prop]);
      if (typeof object[prop] === 'undefined' || object[prop] === null) {
        value = '';
      }

      // Add non-empty property as part of key
      if (propName && value !== '') {
        if (key) {
          key += '_';
        }

        key += propName + ':' + value;
      }
    });

    return key;
  }

  getDataAndPopulate = async (index: number, paramObj: any, populateOnly: boolean = false) => {
    const allSelects = Object.keys(this.#state).map(key => this.dom[key]) as HTMLSelectElement[];

    const doNextSelect = (paramObj, resultsParamObj?: any) => {
      const nextSelect = allSelects[index + 1];

      if (!populateOnly) {
        this.populateResults();
      }

      allSelects.forEach((select, i) => {
        if (select.value === '' && i === 0) {
          this.onFormReset();
        }

        if (i > index) {
          select.disabled = true;

          // Remove all options except the empty value one
          Array.from(select.options).filter((opt) => opt.value !== '').forEach((opt) => {
            opt.remove();
          });

          if (!populateOnly) {
            const key = select.name;
            // delete previous choices from paramObj.
            delete paramObj[key];
          }
        }
      });

      // Check sessionstorage for pre-loaded data, if it's not prevented by request.
      const data = document.location.href.indexOf('flushCache') !== -1 ? false : this.getSessionData(paramObj);

      // choose if select should be opened or not
      // do not open if we're only populating or next select is "vehicle";
      var psCallBack = populateOnly || nextSelect.name === 'vehicle' ? function () { } : () => {
       /*  setTimeout(() => {
          this.openDropdown(nextSelect.name);
        }, 100); */
      };

      if (data) {
        this.populateSelect(nextSelect, data, psCallBack);
        this.checkSubmitState();
        if (resultsParamObj) {
          doResults(resultsParamObj);
        }
      } else {
        // Set loading state for next select
        this.loadingSelect(nextSelect, true);
        // add lang param to paramObj
        const data = {
          ...paramObj,
          lang: document.documentElement.lang,
        };

        this.fetchData(data)
          .then((data: any) => {
            if (data === null) {
              this.populateSelect(nextSelect, null);
            } else {
              // success with data
              if (index === 0) {
                // We're dealing with years. We want them in descending order.
                var byYear = data.data;
                byYear.sort(function (a, b) {
                  if (a.text < b.text) { return 1; }
                  else { return -1; }
                });
              }

              this.storeSessionData(data, paramObj);
              this.populateSelect(nextSelect, data, psCallBack);
              this.checkSubmitState();

              if (resultsParamObj) {
                doResults(resultsParamObj);
              }
            }
          })
          .catch(error => {
            console.error('[TyreSelectorByModel]: failed to fetch data:', error);
            this.populateSelect(nextSelect, null);
          });

      }
    }

    const doResults = (paramObj) => {
      //TODO
      console.log('[TyreSelectorByModel]: doResults', paramObj);
      let sizesForVehicle;
      const selectEl = allSelects[index];
      this.loadingSelect(selectEl, true);

      function vehicleChosen(paramObj?: any) {
        if (paramObj && paramObj.vehicle) return true;
        if (paramObj && !paramObj.vehicle) return false;

        return false; //TODO
        //else return (window.cookieObj && window.cookieObj.vehicle ? true : false);
      }

      // check sessionstorage for pre-loaded data, if it's not prevented by request.
      const data = document.location.href.indexOf('flushCache') !== -1 ? false : this.getSessionData(paramObj);

      if (data) {
        // Do not parse model's tyresizes if user has chosen a vehicle
        if (!vehicleChosen(paramObj) && vehicleChosen()) {
          console.warn('skipping model\'s sizes');
        } else {
          this.populateResults(data, null, paramObj);
          this.loadingSelect(selectEl, false);
        }
      } else {
        // add lang param to paramObj
        const paramData = {
          ...paramObj,
          lang: document.documentElement.lang,
        };

        this.fetchData(paramData)
          .then((data: any) => {
            sizesForVehicle = data;

            // NO RESULTS AT ALL
            // replace result object with htmlsting containing text 'tyre sizes not available'
            if (sizesForVehicle === null || (typeof (sizesForVehicle) === 'object' && sizesForVehicle.data && sizesForVehicle.data.length === 0)) {
              sizesForVehicle = this.parent.loc.tyreselector_no_size_data || 'No tyre size data available for this vehicle.';
            }

            // REMOVE INVALID RESULTS
            // Driveright can actually send this kind of result: "145/R10" (from: Austin/1978/Mini(1958-1993)/Cooper S(55kW))
            // We do not need such nonsense, so loop thru results and check if any of width/height/rim/loadIndex/speedRating info is missing, and remove that result.
            if (typeof (sizesForVehicle) === 'object' && typeof (sizesForVehicle.data) !== 'undefined' && sizesForVehicle.data.length) {
              var resArr = sizesForVehicle.data;
              var i,
                j;

              for (i = 0; i < resArr.length; i++) {
                var txtCount = typeof (resArr[i].text) !== 'undefined' ? resArr[i].text.length : 0;

                // check that we have correct number of values in array
                if (paramObj.mode === 'tyres' && txtCount !== 5) {
                  resArr.splice(i, 1);
                }
                else {
                  // loop thru texts, check that they have values.
                  for (j = 0; j < txtCount; j++) {
                    if (!resArr[i] || !resArr[i].text || !resArr[i].text[j]) {
                      // value is missing, ditch this result!
                      console.warn('ditching invalid ajax result. some of the required texts are missing:', resArr[i]);
                      resArr.splice(i, 1);
                    }
                  }
                }
              } // end loop

              // No valid results left after removing invalid results:
              // replace result object with htmlsting containing text 'tyre sizes not available'
              if (!resArr.length) {
                sizesForVehicle = '<p>' + this.parent.loc.tyreselector_no_size_data || 'No tyre size data available for this vehicle.' + '</p>';
              }
              else {
                // store valid results
                sizesForVehicle.data = resArr;
              }
            }

            // if we have valid data, let's store results
            if (typeof sizesForVehicle !== 'string') {
              this.storeSessionData(sizesForVehicle, paramObj);
            }

            // If current request was for all model's tyresizes, and user has after that chosen a specific vehicle, then do not parse results.
            // This check is done to prevent a situation where model request populates results AFTER user has chosen a vechile: user gets wrong results.

            if (!vehicleChosen(paramObj) && vehicleChosen()) {
              void (0); // do nuthin
            } else {
              // new on tyreselector v3
              // populate results, sizesforVechicle contains data of default sizes, so no need for separate ajax query
              this.populateResults(sizesForVehicle, null, paramObj);
            }

            this.loadingSelect(selectEl, false);
          })
          .catch(error => {
            console.error('[TyreSelectorByModel]: failed to fetch data:', error);
            this.loadingSelect(selectEl, false);

            // get localized error msgs if any
            var errorMsg = this.parent.loc.tyreselector_try_again_later || ' Please try again later. Sorry for the inconvenience.';

            sizesForVehicle = errorMsg ? errorMsg : '';
            this.populateResults(sizesForVehicle, null, paramObj);
          });
      }
    }

    const doModelSizes = async (paramObj) => {
      const selectEl = allSelects[index];

      this.loadingSelect(selectEl, true);
      // add lang param to paramObj
      const fetchData = {
        ...paramObj,
        lang: document.documentElement.lang,
      };

      // check sessionstorage for pre-loaded data, if it's not prevented by request.
      const data = document.location.href.indexOf('flushCache') !== -1 ? false : this.getSessionData(paramObj);

      return new Promise((resolve) => {
        if (data) {
          this.populateResults(data, null, paramObj);
          this.loadingSelect(selectEl, false);
          resolve(true);
        } else {
          this.fetchData(fetchData)
            .then((data: any) => {
              if (data !== null && data.data?.length > 0) {
                this.sizesResults = data.data;
                this.checkSubmitState();
                this.storeSessionData(data, paramObj);
                resolve(true)
              }
              this.loadingSelect(selectEl, false);
              resolve(false);
            })
            .catch(error => {
              console.error('[TyreSelectorByModel]: failed to fetch data:', error);
              this.loadingSelect(selectEl, false);
              resolve(false)
            });
        }
      })
    }

    if (index === 3) {
      // If last select, populate results
      doResults(paramObj);
    } else if (index === 2) {
      // If on model, first get sizes by model
      const hasSizes = await doModelSizes({ ...paramObj, mode: 'tyres' });
      if (hasSizes) {
        // If results were found for modelId, enable engine selection
        doNextSelect(paramObj)
      } else {
        // otherwise show error
        const sizesForVehicle = '<p>' + this.parent.loc.tyreselector_no_model_data || 'No tyre size data available for this model.' + '</p>';
        this.populateResults(sizesForVehicle, null, { ...paramObj, mode: 'tyres' });
        const engineSelect = allSelects[index + 1];
        engineSelect.disabled = true;
      }
    } else {
      // Enable + populate next select
      doNextSelect(paramObj);
    }
  }

  populateSelect(select: HTMLSelectElement, data?: DrResponse | null, callback?: Function) {
    const optGroups = select.querySelectorAll('optgroup');
    const targetEl = optGroups.length > 1 ? optGroups[1] : select;

    if (!data) {
      // No data clears the select
      // Remove old options
      Array.from(select.options).filter((opt) => opt.value !== '').forEach((opt) => {
        opt.remove();
      });
      this.loadingSelect(select, false);
      select.disabled = false;
    } else {
      const state = this.getState();
      // Create new options
      let htmlStr = '';
      data.data.forEach((option) => {
        // "text" might be just a string or an array of info.
        // for example: on "engine" dropdown "text" is an array. It also contains info about vehicle type.
        // if such data exists, write it to <option> element as an extra attribute
        const textType = typeof option.text;
        let textStr = textType !== 'object' && textType !== 'undefined' && option.text !== null ? option.text : '';
        let extraData = '';

        if (!textStr && textType !== 'undefined' && option.text !== null && option.text.length) {
          textStr = option.text[0];
          extraData = option.text[1];
        }

        if (extraData !== '') {
          extraData = 'data-extra="' + extraData + '" ';
        }
        htmlStr += '<option ' + extraData + ' value="' + option.value + '">' + textStr + '</option>';
      });

      // Remove old options if any
      if (targetEl.tagName === 'SELECT') {
        Array.from(targetEl.querySelectorAll('option')).filter((opt) => opt.value !== '').forEach((opt) => {
          opt.remove();
        });
      } else {
        targetEl.querySelectorAll('option').forEach((opt) => {
          opt.remove();
        });
      }

      // Insert new options
      targetEl.insertAdjacentHTML('beforeend', htmlStr);
      // hide throbber
      this.loadingSelect(select, false);
      // enable next select
      select.disabled = false;

      if (state[select.name]) {
        select.value = state[select.name];
      }
    }

    // Trigger native event on all elements that were changed
    // Combobox needs this, if it's used
    this.#updatingFilters = true;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#updatingFilters = false;

    if (typeof callback === 'function') {
      callback();
    }
  }

  populateResults(data?: any, defaultSizeData?: any, paramObj?: any) {
    console.log('[TyreSelectorByModel]: populateResults', { data, defaultSizeData, paramObj });

    this.showErrorMessage(false);

    if (!data) {
      this.checkSubmitState();
    } else if (typeof (data) === 'string' || !data.data) {
      // Driveright data is missing, show string containing message
      let errorMessage = '';
      if (typeof (data) === 'string') {
        errorMessage = data;
      } else {
        errorMessage = this.parent.loc.tyreselector_no_size_data || 'No tyre size data available for this vehicle.';
      }

      this.showErrorMessage(errorMessage);

      this.sizesResults = [];
      this.checkSubmitState(errorMessage);
    } else {
      // Data exists
      var resultArray = data.data;

      // Sort resultArray by sort by RIM
      function sortByRim(arr, key) {
        return arr.sort(function (a, b) {
          return (a[key][2] > b[key][2]) ? 1 : (a[key][2] < b[key][2]) ? -1 : 0;
        });
      }
      sortByRim(resultArray, 'text');

      this.sizesResults = resultArray;
      this.checkSubmitState();
    }
  }

  /**
   * Populates all selects if previously saved state exists
   * @param state
   */
  populateForm(state: State) {


    this.initValues.selectValues = [];
    this.initValues.selectKeys = [];

    if (state.manufacturer) {
      this.initValues.selectValues.push(state.manufacturer);
      this.initValues.selectKeys.push('manufacturer');
    }

    if (state.year) {
      this.initValues.selectValues.push(state.year);
      this.initValues.selectKeys.push('year');
    }

    if (state.model) {
      this.initValues.selectValues.push(state.model);
      this.initValues.selectKeys.push('model');
    }

    if (state.vehicle) {
      this.initValues.selectValues.push(state.vehicle);
      this.initValues.selectKeys.push('vehicle');
    }

    console.log('[TyreSelectorByModel]: populateForm', { state, initValues: this.initValues });

    // if values were found, start populating selects and results
    for (let x = 0; x < this.initValues.selectValues.length; x++) {

      // first select ('manufacturer') gets it's values straight from HTML, so we only have to select correct value for it here.
      // other selects are dynamically updated and they get their "selected" values during population
      if (x === 0 && state.manufacturer) {
        var manufVal = state.manufacturer;

        const selectedOption = Array.from(this.dom.manufacturer.options).find(option => option.value === manufVal);
        if (selectedOption) {
          selectedOption.selected = true;
        }
      }

      const populateOnly = true; // do not disable selects during population and do not modify cookie data.

      // selects are populated by using settings objects.
      var initObj = {
        'stage-language': this.parent.selectedLang,
        tyreSelectorVersion: this.parent.version,
        mode: this.drModes[x],
      };

      for (let j = 0; j <= x; j++) {
        initObj[this.initValues.selectKeys[j]] = this.initValues.selectValues[j];
      }

      // settings done, now populate selects
      this.getDataAndPopulate(x, initObj, populateOnly);
    }

    // Disable selects that are not populated
    const allSelects = Object.keys(this.#state).map(key => this.dom[key]) as HTMLSelectElement[];
    allSelects.forEach((select, i) => {
      if (i > this.initValues.selectValues.length) {
        select.disabled = true;
      }
    });

  }

  loadingSelect(select: HTMLSelectElement, state: boolean) {
    if (state) {
      select.classList.add('loading');
    } else {
      select.classList.remove('loading');
    }
  }

  showErrorMessage(message: string | boolean) {
    if (typeof message === 'string' && message.length) {
      const textElement = this.dom.errorMessage.querySelector('[data-ts-error-message-text]');
      if (textElement) {
        textElement.innerHTML = message;
        this.dom.errorMessage.hidden = false;
      }

    } else {
      // No message, treat as if clearing message.
      this.dom.errorMessage.hidden = true;
    }
  }

  /**
   * Updates submit button state if all fields have a value
   */
  checkSubmitState(errorMessage?: string) {
    const state = this.getState();
    const requiredFieldsHaveValue = state.manufacturer && state.year && state.model;

    console.log('[TyreSelectorByModel]: checkSubmitState', { requiredFieldsHaveValue, sizesResults: this.sizesResults, state: this.getState() });

    if (requiredFieldsHaveValue && this.sizesResults.length && !errorMessage) {
      this.setSubmitActive(true);
    } else {
      this.setSubmitActive(false);
    }
  }

  /** Set submit button disabled state */
  setSubmitActive(active: boolean) {
    const submitBtn = this.dom.form.querySelector('button[type="submit"]') as HTMLButtonElement;
    submitBtn.disabled = !active;
  }
  /**
   * Fetches data from server
   */
  fetchData(data: DrData) {
    console.log('[TyreSelectorByModel]: fetchData', data);
    const params = new URLSearchParams(data).toString();

    this.controller.abort();
    this.controller = new AbortController();

    return fetch('/stage-ajax/driveRight?' + params,
      {
        method: 'GET',
        signal: this.controller.signal,
      }
    )
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .catch(error => {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('[TyreSelectorByModel]: failed to fetch data:', error);
        } else {
          return null;
        }
        return Promise.reject(error);
      });
  }

  doAction(data) {
    const formState = this.getState();
    console.log('[TyreSelectorByModel]: doAction', { data, formState });

    if (['embed', 'default'].includes(this.parent.type)) {
      // If embed, redirect to size page

      // Check if there are any available sizes
      const availableSizes: any[] = [];
      console.log(this.parent.sizeData)
      data.forEach(resultItem => {
        const resultSize = resultItem.text;
        // Check availability by comparing to sizeData
        const matches = this.parent.sizeData.filter(availableSize => {
          const match = resultSize[0] === availableSize[0] && resultSize[1] === availableSize[1] && resultSize[2] === availableSize[2];
          return match;
        });

        if (matches.length) {
          availableSizes.push({
            size: resultItem.value,
            width: resultSize[0],
            height: resultSize[1],
            rim: resultSize[2],
            default: resultItem.default,
          });
        }
      });

      if (availableSizes.length) {
        let redirectSize = availableSizes[0];

        // If manufacturers default size exists, use it
        const defaultSize = availableSizes.find(size => size.default);
        if (defaultSize) {
          redirectSize = defaultSize;
        }

        const { width, height, rim } = redirectSize;

        // Store tyre data
        // This needs selected option value and text
        const tyreDataValues: any = {};
        Object.keys(formState).forEach(key => {
          const value = formState[key];
          const select = this.dom[key] as HTMLSelectElement;
          const selectedOption = Array.from(select.options).find(option => option.value === value);
          tyreDataValues[key] = {
            value: value,
            text: selectedOption?.textContent || '',
          };
        });

        // Get rim size range from items
        const rimSizes = availableSizes.map(size => size.rim).sort();

        //Store tyredata
        this.parent.storeTyreData({
          width: width,
          height: height,
          rim: rim,
          manufacturer: tyreDataValues.manufacturer,
          model: tyreDataValues.model,
          year: tyreDataValues.year,
          vehicle: tyreDataValues.vehicle,
          availableSizes,
          rimSizeRange: `${rimSizes[0]}${rimSizes.length > 1 ? `-${rimSizes[rimSizes.length - 1]}` : ''}` // rimSizeRange from items
        }, 'model');

        this.parent.setVisibility('hidden');

        const analyticsEvent = {
          event: 'tyre_search_submit',
          tyre_search_type: 'Model Search',
          tyre_size: `${width}/${height}R${rim}`,
          vehicle_manufacturer: tyreDataValues.manufacturer.text,
          vehicle_year: tyreDataValues.year.text,
          vehicle_model: tyreDataValues.model.text,
          vehicle_engine: tyreDataValues.vehicle.text,
        }

        this.parent.setView('model');

        const extraParams: { noEngine?: boolean } = {};
        if (!tyreDataValues.vehicle.value) {
          extraParams.noEngine = true;
        }

        this.parent.redirectToSizePage(width, height, rim, { analyticsEvent, extraParams });
      } else {
        //TODO: no sizes available, maybe show message?
        console.log('[TyreSelectorByModel]: no sizes available', { data, availableSizes });
      }

    } else {
      //TODO
    }
  }

  /**
   * Notification bar has buttons to reset part of the state or focus dropdowns
   * @param target either 'size', 'model' or 'vehicle'
   */
  onNotificationClick(target) {
    try {
      switch (target) {
        case 'size': // reset all
          this.onFormReset();
          this.parent.show();
          setTimeout(() => {
            this.openDropdown('manufacturer');
          }, 300);
          break;
        case 'model': // reset model view and parent tyreData
          const existingState = this.getState();
          existingState.model = null;
          this.updateState(existingState);
          this.populateForm(existingState);

          const tyreData = JSON.parse(sessionStorage.getItem('tyreData') || '{}');
          this.parent.storeTyreData({
            ...tyreData,
            model: { value: null, text: '' },
            availableSizes: [],
          }, 'model');
          sessionStorage.setItem(this.#storageKey, JSON.stringify(existingState));

          this.parent.show();
          setTimeout(() => {
            this.openDropdown('model');
          }, 300);
          break;
        case 'manufacturer':
            this.parent.show();
            setTimeout(() => {
              this.openDropdown('manufacturer');
            }, 300);
            break;
        case 'vehicle':
          this.parent.show();
          setTimeout(() => {
            this.openDropdown('vehicle');
          }, 300);
          break;
        default:
      }
    } catch (e) { }
  }

  openDropdown(dropdown) {
    if (this.#comboboxes[dropdown]) {
      this.#comboboxes[dropdown].open(true);
    }
  }
}
